/**
 * Project share-link DTOs.
 *
 * Mirrors Prisma `ProjectShareLink` — a CSPRNG-tokened link to a project's
 * read-only client/designer pack at `/shared/[token]`. Multiple active links
 * per project; `accessCount` is the view tally; `revokedAt` soft-revokes.
 *
 * Owners and admins list and revoke EVERY link on a project (AUTH-2), so each
 * link says who manages it now.
 */
import { z } from "zod";

/**
 * Who manages a link NOW. Initially its creator; when a team member is removed
 * their links pass to the account owner, who then manages them. It is not a
 * record of who created the link.
 */
export const ProjectShareLinkManagerSchema = z.object({
  userId: z.string(),
  email: z.string(),
  name: z.string().nullable(),
});
export type ProjectShareLinkManager = z.infer<typeof ProjectShareLinkManagerSchema>;

export const ProjectShareLinkSchema = z.object({
  id: z.string(),
  token: z.string(),
  /** Full public URL — `${BASE}/shared/${token}`. */
  url: z.string(),
  label: z.string().nullable(),
  accessCount: z.number(),
  revokedAt: z.string().datetime().nullable(),
  expiresAt: z.string().datetime().nullable(),
  createdAt: z.string().datetime(),
  /**
   * Who manages the link now (see the schema above). Optional on the wire so an
   * app parses a server that predates it; the server always sends it.
   */
  managedBy: ProjectShareLinkManagerSchema.optional(),
});
export type ProjectShareLink = z.infer<typeof ProjectShareLinkSchema>;

// #2411 server-boundary variant: the mobile app parses the permissive base;
// the routes serialise through the Strict form.
export const ProjectShareLinkStrict = ProjectShareLinkSchema.strict();
